import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardOverview from './admin/DashboardOverview';
import ProductsManagement from './admin/ProductsManagement';
import OrdersManagement from './admin/OrdersManagement';
import InventoryManagement from './admin/InventoryManagement';
import DealersManagement from './admin/DealersManagement';
import DistributorsManagement from './admin/DistributorsManagement';
import InvestmentsManagement from './admin/InvestmentsManagement';
import CommissionsManagement from './admin/CommissionsManagement';
import JobsManagement from './admin/JobsManagement';
import BlogManagement from './admin/BlogManagement';
import CMSManagement from './admin/CMSManagement';
import CouponsManagement from './admin/CouponsManagement';
import InvoicesManagement from './admin/InvoicesManagement';
import ReportsManagement from './admin/ReportsManagement';
import NotificationsManagement from './admin/NotificationsManagement';
import AdsManagement from './admin/AdsManagement';
import NewsletterManagement from './admin/NewsletterManagement';
import SocialMediaManagement from './admin/SocialMediaManagement';
import UsersManagement from './admin/UsersManagement';
import SettingsManagement from './admin/SettingsManagement';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'products', label: 'Products', icon: '📦' },
    { id: 'cms', label: 'CMS', icon: '🧩' },
    { id: 'orders', label: 'Orders', icon: '🛒' },
    { id: 'inventory', label: 'Inventory', icon: '🏭' },
    { id: 'dealers', label: 'Dealers', icon: '🤝' },
    { id: 'distributors', label: 'Distributors', icon: '🌐' },
    { id: 'investments', label: 'Investments', icon: '💼' },
    { id: 'commissions', label: 'Commissions', icon: '💰' },
    { id: 'jobs', label: 'Jobs', icon: '💼' },
    { id: 'blogs', label: 'Blogs', icon: '📝' },
    { id: 'coupons', label: 'Coupons', icon: '🎟️' },
    { id: 'invoices', label: 'Invoices', icon: '🧾' },
    { id: 'reports', label: 'Reports', icon: '📈' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'ads', label: 'Ads', icon: '📢' },
    { id: 'newsletters', label: 'Newsletters', icon: '📧' },
    { id: 'social', label: 'Social Media', icon: '📱' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'products':
        return <ProductsManagement />;
      case 'cms':
        return <CMSManagement />;
      case 'orders':
        return <OrdersManagement />;
      case 'inventory':
        return <InventoryManagement />;
      case 'dealers':
        return <DealersManagement />;
      case 'distributors':
        return <DistributorsManagement />;
      case 'investments':
        return <InvestmentsManagement />;
      case 'commissions':
        return <CommissionsManagement />;
      case 'jobs':
        return <JobsManagement />;
      case 'blogs':
        return <BlogManagement />;
      case 'coupons':
        return <CouponsManagement />;
      case 'invoices':
        return <InvoicesManagement />;
      case 'reports':
        return <ReportsManagement />;
      case 'notifications':
        return <NotificationsManagement />;
      case 'ads':
        return <AdsManagement />;
      case 'newsletters':
        return <NewsletterManagement />;
      case 'social':
        return <SocialMediaManagement />;
      case 'users':
        return <UsersManagement />;
      case 'settings':
        return <SettingsManagement />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">Ortus Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {user?.name || 'Admin'}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white shadow-md min-h-screen">
          <div className="p-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left px-4 py-3 rounded-md mb-2 transition ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
